import {fetchUtils, InputProps, useInput} from 'react-admin';
import {TextField} from '@mui/material';
import React, {Suspense, useEffect} from "react";
import "toastui-editor/style.css";
import CustomLoadingComponent from "./CustomLoadingComponent";
import {LOCAL_STORAGE_KEY} from "../types";
import {ToastUiEditor} from "toastui-editor";

export type TipTapEditorInputProps = {
    initialValue?: string;
    onChangeHtml?: (html: string) => void;
    onChangeMarkdown?: (markdown: string) => void;
    itemId?: string;
    editing?: boolean;
} & InputProps;

const ToastUiEditorInput = (props: TipTapEditorInputProps) => {
    const [isFullScreen, setIsFullScreen] = React.useState(false);
    const {initialValue, onChangeMarkdown, itemId, editing = false} = props;
    const [uploadingImage, setUploadingImage] = React.useState<boolean>(false);
    // 使用 useInput 來獲取控制字段的各種方法和屬性
    const {
        field,        // field 包含了 value 和 onChange 方法
        fieldState,   // fieldState 包含了 isTouched, isDirty 等狀態
        formState,    // formState 包含整個表單的狀態
        id,
    } = useInput(props);

    useEffect(() => {
        if(!editing){
            const _itemId = localStorage.getItem(LOCAL_STORAGE_KEY.BLOGCTRBITEM_CREATE_UNIQUEID);
            if(!_itemId){
                fetchUniqueId().then(id=>{
                    if (id) {
                        localStorage.setItem(LOCAL_STORAGE_KEY.BLOGCTRBITEM_CREATE_UNIQUEID,id);
                    }
                })
            }
        }
    }, []);

    // 處理手動設置值的函數
    const handleSetTextContentValue = (content: string) => {
        field.onChange(content);
    };

    const fetchUniqueId = async (): Promise<string> => {
        return await fetchUtils.fetchJson('/api/blogctrbitems/uniqueid',{
            method: 'GET',
            headers: new Headers({
                'Authorization': `Bearer ${localStorage.getItem('user')? JSON.parse(localStorage.getItem('user') as string)?.token : ''}`,
            }),
        })
            .then(({ json }) => {
                if(json && json.status === 200){
                    return json.data;
                }
            })
            .catch(error => {
                throw error;
            });
    }

    const handleUploadImage = async (file: File): Promise<string> => {
        const formData = new FormData();
        const uniqueId = localStorage.getItem(LOCAL_STORAGE_KEY.BLOGCTRBITEM_CREATE_UNIQUEID);
        formData.append('imageFile', file);
        if(editing){
            itemId && formData.append('itemId', itemId);
        } else {
            uniqueId && formData.append('itemId', uniqueId);
        }
        return await fetchUtils.fetchJson('/api/cms/uploadarticleimage',{
            method: 'POST',
            body: formData,
            headers: new Headers({
                'Authorization': `Bearer ${localStorage.getItem('user')? JSON.parse(localStorage.getItem('user') as string)?.token : ''}`,
            }),
        })
            .then(({ json }) => {
                if(json && json.status === 200){
                    return json.data;
                }
            })
            .catch(error => {
                throw error;
            });
    }

    const callbackUploadImage = async (file: File): Promise<string> =>  {
        setUploadingImage(true);
        const url = await handleUploadImage(file);
        setUploadingImage(false);
        return url;
    }

    const saveSrcContentLocalStorage = (value: string) => {
        localStorage.setItem(LOCAL_STORAGE_KEY.BLOGCTRBITEM_CREATE_SRCCONTENT_HISTORY, value);
    }

    const saveTextContentLocalStorage = (value: string) => {
        localStorage.setItem(LOCAL_STORAGE_KEY.BLOGCTRBITEM_CREATE_TEXTCONTENT_HISTORY, value);
    }

    return (
        <div className={'w-full overflow-hidden'}>
            <TextField
                {...field} // 繫結輸入的 value 和 onChange
                label={props.label}       // 傳遞 label
                error={!!fieldState.error} // 顯示錯誤
                helperText={fieldState.error?.message} // 顯示錯誤信息
                fullWidth
                value={initialValue || ''}
                style={{display: 'none'}}
            />
            <div className="flex w-full flex-col">
                <span className={`w-[80px] ${fieldState.error ? 'text-red-500' : 'text-gray-500'}`}>{props.label}*</span>
                <div className={`${
                    isFullScreen
                        ? 'top-10 left-0 w-full h-full p-4 absolute bg-white z-50'
                        : 'grow w-auto relative'
                }`}>
                    <Suspense fallback={<div>Loading...</div>}>
                        <ToastUiEditor initialValue={initialValue || ''} onChangeHtml={(m)=>{
                            handleSetTextContentValue(m);
                            !editing && saveTextContentLocalStorage(m);
                        }}
                        onChangeFullScreen={(f)=>setIsFullScreen(f)} uploadImage={callbackUploadImage}
                        onChangeMarkdown={(markdown)=>{
                           onChangeMarkdown?.(markdown);
                           !editing && saveSrcContentLocalStorage(markdown);
                        }}/>
                    </Suspense>
                    {uploadingImage && <CustomLoadingComponent/>}
                </div>
            </div>
            {(fieldState.error) && <span className={'text-red-500 text-xs'}>*必須填寫文章內文</span>}
        </div>
    );
};

export default ToastUiEditorInput;
